import { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { enqueueSnackbar } from 'notistack'
import { MdRefresh } from 'react-icons/md'
import { IAppStore } from '@/models/store.model'
import { IConfigSeller } from '@/models/configSeller'
import { webApiService } from '@/services'
import { Button } from '@/components/ui/button'
import { useTradeinContext } from '../hooks/useTradeinContext'
import AddContribution from './AddContribution'
import ResendCode from './ResendCode'
import MultipleReceipt from './MultipleReceipt'
import Recalculate from './Recalculate'
import CheckFeasibility from './CheckFeasibility'
import InWarehouse from './InWarehouse'
import ForwardGiftcard from './ForwardGiftcard'
import ForwardGiftcardRipley from './ForwardGiftcardRipley'
import CancelTradein from './CancelTradein'
import AddDocument from './AddDocument'

interface TradeInActionsToolbarProps {
  /* 'danger' = solo "Rechazar Tradein" (va fijo abajo a la izquierda del footer).
     'other' = todo el resto (va abajo a la derecha, como un modal footer). */
  variant: 'danger' | 'other'
}

/* Réplica de las condiciones de Header.tsx (mismos ~10 botones administrativos: rechazar,
   recalcular, reenviar código, reenviar giftcard, agregar documento/aporte, etc.), para que
   sigan disponibles dentro del modal de progreso. Se excluyen del original: el botón "Volver"
   (no aplica dentro de un modal) y "Recibir equipo"/"Verificar Código", que ya viven como
   pasos propios del modal (ReceiveCard/PhonecheckCard). */
const TradeInActionsToolbar: FC<TradeInActionsToolbarProps> = ({ variant }) => {
  const { ctx, saveCtx } = useTradeinContext()
  const userState = useSelector((store: IAppStore) => store.user)
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const [novabenchLoading, setNovabenchLoading] = useState(false)

  const isCoppel = configSeller.config.s3.FOLDER_APP === 'ti_coppel'
  const isFalabellaChile = configSeller.config.s3.FOLDER_APP === 'ti_falabella'
  const isRipleyChile = configSeller.config.s3.FOLDER_APP === 'ti_ripley_cl'
  const isMacstoreMexico = configSeller.config.s3.FOLDER_APP === 'ti_macstore'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isXiaomi = configSeller.config.s3.FOLDER_APP === 'ti_xiaomi'
  const isWom = configSeller.config.s3.FOLDER_APP === 'ti_wom'

  const allowedEmails = (import.meta.env.VITE_CAN_CANCEL_TRADEIN || '')
    .split(',')
    .map((email: string) => email.trim())

  const handleSerieSave = async () => {
    setNovabenchLoading(true)
    if (import.meta.env.VITE_COUNTRY === 'CL') {
      await webApiService.checkTestCL(String(ctx.tradein.login_code_novabench), String(ctx.tradein.id))
        .catch((error) => {
          enqueueSnackbar('Hubo un error al intentar revisar Novabench.', { variant: 'error' })
          console.log('error ==>', error)
        }).finally(() => {
          saveCtx({ ...ctx, reload: true })
          setNovabenchLoading(false)
        })
    }
    if (import.meta.env.VITE_COUNTRY === 'MX') {
      await webApiService.checkTestMX(String(ctx.tradein.login_code_novabench), String(ctx.tradein.id))
        .catch((error) => {
          enqueueSnackbar('Hubo un error al intentar revisar Novabench.', { variant: 'error' })
          console.log('error ==>', error)
        }).finally(() => {
          saveCtx({ ...ctx, reload: true })
          setNovabenchLoading(false)
        })
    }
  }

  const buttons: Array<{ key: string, visible: boolean, node: React.ReactNode }> = [
    { key: 'contribution', visible: true, node: <AddContribution /> },
    {
      key: 'resend-code',
      visible: ctx.tradein.sbu === 'Smartphone' && ctx.tradein.id_status === 6 && ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)].includes(userState.id_role),
      node: <ResendCode />
    },
    {
      key: 'multiple-receipt',
      visible: (isCoppel || isMacstoreMexico) && ctx.tradein.rut !== '' && ctx.tradein.id_status === 3 && ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)].includes(userState.id_role),
      node: <MultipleReceipt />
    },
    {
      key: 'novabench',
      visible: (isFalabellaChile || isMacstoreMexico || isRipleyChile) && ctx.tradein.id_status === 1 && ctx.tradein.sbu === 'Notebook',
      node: (
        <Button variant='secondary' size='sm' onClick={handleSerieSave} disabled={novabenchLoading}>
          <MdRefresh />
          {novabenchLoading ? 'Revisando...' : 'Revisar Novabench'}
        </Button>
      )
    },
    {
      key: 'recalculate',
      visible: ctx.tradein.recalculate === '1' && ctx.tradein.wasRecalculated === null && ctx.tradein.id_status !== 8 && ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)].includes(userState.id_role),
      node: <Recalculate />
    },
    {
      key: 'check-feasibility',
      visible: ((ctx.tradein.certificate_theft === 'VALID' && ctx.tradein.cancel_reason === 'El modelo evaluado no esta disponible' && ctx.tradein.grade_IA === null && ctx.tradein.id_status === 8) ||
        (ctx.tradein.certificate_theft === 'VALID' && ctx.tradein.cancel_reason === 'El modelo evaluado no esta disponible' && ctx.tradein.grade_IA === null && ctx.tradein.id_status === 11 && isHuaweiOnlineChile)) &&
        (ctx.tradein.sbu === 'Smartphone' || ctx.tradein.sbu === 'Tablet') && ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)].includes(userState.id_role),
      node: <CheckFeasibility />
    },
    {
      key: 'in-warehouse',
      visible: ctx.tradein.id_status === 4 && ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)].includes(userState.id_role) && (configSeller.config.s3.FOLDER_APP === 'ti_samsung_pe' || configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'),
      node: <InWarehouse />
    },
    {
      key: 'forward-giftcard',
      visible: configSeller.config.s3.FOLDER_APP === 'ti_falabella' && [5, 7, 9].includes(ctx.tradein.id_status),
      node: <ForwardGiftcard />
    },
    {
      key: 'forward-giftcard-ripley',
      visible: configSeller.config.s3.FOLDER_APP === 'ti_ripley_cl' && [5, 7, 9].includes(ctx.tradein.id_status),
      node: <ForwardGiftcardRipley />
    },
    {
      key: 'cancel-tradein',
      visible: (ctx.tradein.id_status < 5 && ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)].includes(userState.id_role)) ||
        ([1, 2, 3, 4, 5, 6, 9, 10].includes(ctx.tradein.id_status) && allowedEmails.includes(userState.email)) ||
        ([Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION)].includes(userState.id_role) && ctx.tradein.id_status >= 5),
      node: <CancelTradein />
    },
    {
      key: 'add-document',
      visible: ctx.tradein.id_status >= 5 && ![Number(import.meta.env.VITE_PERFIL_SOLOLECTURA)].includes(userState.id_role) && (ctx.tradein.document_cupon_used === null || userState.id_role === Number(import.meta.env.VITE_PERFIL_ADMIN)) && (isWom || isXiaomi),
      node: <AddDocument />
    }
  ]

  const visibleButtons = buttons
    .filter((b) => b.visible)
    .filter((b) => (variant === 'danger' ? b.key === 'cancel-tradein' : b.key !== 'cancel-tradein'))

  if (visibleButtons.length === 0) return null

  return (
    <div className={['flex flex-wrap items-center gap-1.5', variant === 'other' ? 'justify-end' : ''].join(' ')}>
      {visibleButtons.map((b) => <span key={b.key}>{b.node}</span>)}
    </div>
  )
}

export default TradeInActionsToolbar
